import React from "react";
import { useState, useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { SEOHead } from "@/components/SEOHead";
import { Footer } from "@/components/Footer";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Search, ChevronLeft, ChevronRight, TrendingUp, Clock, Users, Sparkles } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { analytics } from "@/lib/analytics";

/* ────────────────────────────────────────────────────
   Startup Blueprints — Ideas Page (Lavender Dream)
   ──────────────────────────────────────────────────── */

const GRADIENT_BG = `
  radial-gradient(ellipse 90% 70% at 5% -5%, rgba(175, 158, 252, 0.6) 0%, transparent 55%),
  radial-gradient(ellipse 70% 60% at 95% 8%, rgba(218, 148, 238, 0.45) 0%, transparent 52%),
  radial-gradient(ellipse 55% 45% at 50% 105%, rgba(155, 195, 245, 0.35) 0%, transparent 55%),
  radial-gradient(ellipse 45% 35% at 85% 55%, rgba(245, 158, 182, 0.28) 0%, transparent 48%),
  #ede9f5
`;

const StartupExplorer = () => {
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState("new");
  const [searchQuery, setSearchQuery] = useState("");
  const [categoryFilter, setCategoryFilter] = useState("all");
  const [currentPage, setCurrentPage] = useState(1);
  const PAGE_SIZE = 12;

  // Check if user has personalization signals
  const { data: hasSignals } = useQuery({
    queryKey: ["has-personalization-signals", user?.id],
    queryFn: async () => {
      if (!user) return false;
      const { data, error } = await supabase.rpc('has_personalization_signals', {
        p_user_id: user.id,
      });
      if (error) return false;
      return data === true;
    },
    enabled: !!user,
    staleTime: 60 * 1000,
  });

  const { data: ideasData, isLoading } = useQuery({
    queryKey: ["ideas", activeTab, categoryFilter, currentPage, user?.id],
    queryFn: async () => {
      const from = (currentPage - 1) * PAGE_SIZE;
      const to = from + PAGE_SIZE - 1;
      if (!user) {
        let query = supabase
          .from("ideas")
          .select("id, title, tagline, description, summary, tags, niche, score, created_at, updated_at, is_public, user_id, market_size_usd, market_size, opportunity_score, pricing_model, problem, mvp_weeks, estimated_arr", { count: 'exact' })
          .eq("is_public", true)
          .order("score", { ascending: false })
          .range(from, to);

        if (categoryFilter !== "all") {
          query = query.eq("niche", categoryFilter);
        }

        const { data, error, count } = await query;
        if (error) throw error;
        return { ideas: data || [], total: count || 0 };
      }

      if (activeTab === "foryou") {
        const rpcParams: Record<string, unknown> = {
          p_user_id: user.id,
          p_limit: PAGE_SIZE,
        };
        if (categoryFilter !== "all") {
          rpcParams.p_niche = categoryFilter;
        }
        const { data, error } = await supabase.rpc('get_recommended_ideas', rpcParams);
        if (error) throw error;
        return { ideas: data || [], total: data?.length || 0 };
      }

      if (activeTab === "saved") {
        const [favResult, uiResult] = await Promise.all([
          supabase.from("favorites").select("idea_id").eq("user_id", user.id),
          supabase.from("user_ideas").select("idea_id").eq("user_id", user.id).eq("status", "saved"),
        ]);
        if (favResult.error) throw favResult.error;
        const favIds = favResult.data?.map(f => f.idea_id) || [];
        const uiIds = uiResult.data?.map(f => f.idea_id) || [];
        const ideaIds = [...new Set([...favIds, ...uiIds])];

        if (ideaIds.length === 0) return { ideas: [], total: 0 };

        let query = supabase
          .from("ideas")
          .select("id, title, tagline, description, summary, tags, niche, score, created_at, updated_at, is_public, user_id, market_size_usd, market_size, opportunity_score, pricing_model, problem, mvp_weeks, estimated_arr", { count: 'exact' })
          .in("id", ideaIds)
          .order("created_at", { ascending: false })
          .range(from, to);

        if (categoryFilter !== "all") {
          query = query.eq("niche", categoryFilter);
        }

        const { data, error, count } = await query;
        if (error) throw error;
        return { ideas: data || [], total: count || 0 };
      }

      if (activeTab === "building" || activeTab === "notinterested") {
        const statusMap: Record<string, 'building' | 'not_interested'> = {
          building: 'building',
          notinterested: 'not_interested'
        };

        const status = statusMap[activeTab];
        if (!status) return { ideas: [], total: 0 };

        const { data: userIdeas, error: userIdeasError } = await supabase
          .from("user_ideas")
          .select("idea_id")
          .eq("user_id", user.id)
          .eq("status", status);

        if (userIdeasError) throw userIdeasError;
        const ideaIds = userIdeas?.map(ui => ui.idea_id) || [];

        if (ideaIds.length === 0) return { ideas: [], total: 0 };

        let query = supabase
          .from("ideas")
          .select("id, title, tagline, description, summary, tags, niche, score, created_at, updated_at, is_public, user_id, market_size_usd, market_size, opportunity_score, pricing_model, problem, mvp_weeks, estimated_arr", { count: 'exact' })
          .in("id", ideaIds)
          .order("created_at", { ascending: false })
          .range(from, to);

        if (categoryFilter !== "all") {
          query = query.eq("niche", categoryFilter);
        }

        const { data, error, count } = await query;
        if (error) throw error;
        return { ideas: data || [], total: count || 0 };
      }

      let query = supabase
        .from("ideas")
        .select("id, title, tagline, description, summary, tags, niche, score, created_at, updated_at, is_public, user_id, market_size_usd, market_size, opportunity_score, pricing_model, problem, mvp_weeks, estimated_arr", { count: 'exact' })
        .eq("is_public", true)
        .order("created_at", { ascending: false })
        .range(from, to);

      if (categoryFilter !== "all") {
        query = query.eq("niche", categoryFilter);
      }

      const { data, error, count } = await query;
      if (error) throw error;
      return { ideas: data || [], total: count || 0 };
    },
  });

  // Featured "Idea of the Day"
  const { data: featuredIdea } = useQuery({
    queryKey: ["featured-idea"],
    queryFn: async () => {
      const today = new Date().toISOString().split("T")[0];
      const { data: featured } = await supabase
        .from("ideas")
        .select("id, title, tagline, niche, estimated_arr, tags, score, opportunity_score, mvp_weeks")
        .eq("featured_date", today)
        .eq("is_public", true)
        .limit(1)
        .single();
      if (featured) return featured;
      const { data: topIdea } = await supabase
        .from("ideas")
        .select("id, title, tagline, niche, estimated_arr, tags, score, opportunity_score, mvp_weeks")
        .eq("is_public", true)
        .order("score", { ascending: false })
        .limit(1)
        .single();
      return topIdea || null;
    },
  });

  // Niche filter options
  const { data: nicheOptions } = useQuery({
    queryKey: ["idea-niches"],
    queryFn: async () => {
      const { data } = await supabase
        .from("ideas")
        .select("niche")
        .eq("is_public", true)
        .not("niche", "is", null);
      if (!data) return [];
      const unique = [...new Set(data.map((d: any) => d.niche).filter(Boolean))].sort();
      return unique as string[];
    },
    staleTime: 5 * 60 * 1000,
  });

  const ideas = ideasData?.ideas || [];
  const totalIdeas = ideasData?.total || 0;
  const totalPages = Math.ceil(totalIdeas / PAGE_SIZE);
  const startIndex = (currentPage - 1) * PAGE_SIZE + 1;
  const endIndex = Math.min(currentPage * PAGE_SIZE, totalIdeas);

  const filteredIdeas = searchQuery.trim()
    ? ideas.filter((idea) =>
        idea.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
        idea.tagline?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        idea.summary?.toLowerCase().includes(searchQuery.toLowerCase())
      )
    : ideas;

  useEffect(() => {
    if (activeTab === "foryou" && hasSignals === false) {
      setActiveTab("new");
    }
  }, [activeTab, hasSignals]);

  const resetToFirstPage = () => setCurrentPage(1);

  useEffect(() => {
    resetToFirstPage();
  }, [activeTab, categoryFilter, searchQuery]);

  const filters = ["All ideas", ...(nicheOptions || []).slice(0, 7)];

  return (
    <div className="min-h-screen" style={{ background: GRADIENT_BG }}>
      <SEOHead
        title="Discover Data-Validated Startup Ideas | DayOne"
        description="Find, validate, and build your next startup with AI-powered market insights."
      />

      <div className="max-w-6xl mx-auto px-6 pt-8 pb-16">
        {/* Header */}
        <div className="text-center mb-8">
          <h1
            className="text-[28px] text-[#18151f] mb-2"
            style={{ fontFamily: "'Instrument Serif', serif" }}
          >
            Startup Blueprints
          </h1>
          <p
            className="text-[13px] text-[#6b5f8a]"
            style={{ fontFamily: "'DM Sans', sans-serif" }}
          >
            {totalIdeas || 150} validated ideas · updated weekly
          </p>
        </div>

        {/* Search Bar */}
        <div className="max-w-[600px] mx-auto mb-6">
          <div
            className="relative rounded-2xl overflow-hidden"
            style={{
              background: "rgba(255, 255, 255, 0.72)",
              backdropFilter: "blur(12px)",
              border: "1px solid rgba(255, 255, 255, 0.85)",
            }}
          >
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-[#6b5f8a]" />
            <input
              type="text"
              placeholder="Search startup ideas..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-12 pr-4 py-4 text-[15px] bg-transparent border-0 focus:outline-none text-[#18151f] placeholder:text-[#6b5f8a]/60"
              style={{ fontFamily: "'DM Sans', sans-serif" }}
            />
          </div>
        </div>

        {/* Filter Pills */}
        <div className="flex flex-wrap justify-center gap-2 mb-8">
          {filters.map((filter) => {
            const isActive = filter === "All ideas" ? categoryFilter === "all" : categoryFilter === filter;
            return (
              <button
                key={filter}
                onClick={() => setCategoryFilter(filter === "All ideas" ? "all" : filter)}
                className="px-4 py-2 text-[13px] rounded-full transition-all"
                style={{
                  fontFamily: "'DM Sans', sans-serif",
                  background: isActive ? "rgba(255, 255, 255, 0.85)" : "rgba(255, 255, 255, 0.5)",
                  border: isActive ? "2px solid #7c6af0" : "1px solid rgba(255, 255, 255, 0.85)",
                  color: isActive ? "#7c6af0" : "#6b5f8a",
                  fontWeight: isActive ? 600 : 400,
                }}
              >
                {filter}
              </button>
            );
          })}
        </div>

        {/* Tabs for authenticated users */}
        {user && (
          <div className="flex justify-center mb-8">
            <Tabs value={activeTab} onValueChange={setActiveTab}>
              <TabsList
                className={`grid ${hasSignals ? 'grid-cols-5' : 'grid-cols-4'} h-11 rounded-full p-1`}
                style={{ background: "rgba(255, 255, 255, 0.6)" }}
              >
                <TabsTrigger
                  value="new"
                  className="text-[13px] rounded-full data-[state=active]:bg-white data-[state=active]:text-[#7c6af0]"
                >
                  New
                </TabsTrigger>
                {hasSignals && (
                  <TabsTrigger
                    value="foryou"
                    className="text-[13px] rounded-full data-[state=active]:bg-white data-[state=active]:text-[#7c6af0]"
                  >
                    For You
                  </TabsTrigger>
                )}
                <TabsTrigger
                  value="saved"
                  className="text-[13px] rounded-full data-[state=active]:bg-white data-[state=active]:text-[#7c6af0]"
                >
                  Saved
                </TabsTrigger>
                <TabsTrigger
                  value="building"
                  className="text-[13px] rounded-full data-[state=active]:bg-white data-[state=active]:text-[#7c6af0]"
                >
                  Building
                </TabsTrigger>
                <TabsTrigger
                  value="notinterested"
                  className="text-[13px] rounded-full data-[state=active]:bg-white data-[state=active]:text-[#7c6af0]"
                >
                  Not Interested
                </TabsTrigger>
              </TabsList>
            </Tabs>
          </div>
        )}

        {/* Results count */}
        <div className="text-center text-[13px] text-[#6b5f8a] mb-6" style={{ fontFamily: "'DM Sans', sans-serif" }}>
          {!isLoading && (
            <span>
              {searchQuery.trim() ? `${filteredIdeas.length} of ${totalIdeas}` : `Showing ${startIndex}–${endIndex} of ${totalIdeas}`} ideas
            </span>
          )}
        </div>

        {/* Featured Idea of the Day */}
        {featuredIdea && activeTab === "new" && !searchQuery.trim() && (
          <div
            className="mb-8 rounded-2xl p-6 relative overflow-hidden"
            style={{
              background: "rgba(255, 255, 255, 0.72)",
              backdropFilter: "blur(12px)",
              border: "2px solid rgba(124, 106, 240, 0.3)",
            }}
          >
            <div className="flex flex-col md:flex-row gap-6">
              {/* Left: ARR projection panel */}
              <div
                className="w-full md:w-[200px] flex-shrink-0 rounded-xl p-4 flex flex-col items-center justify-center"
                style={{ background: "linear-gradient(135deg, rgba(168, 152, 248, 0.3), rgba(204, 130, 232, 0.2))" }}
              >
                <div className="text-[11px] uppercase tracking-wider text-[#7c6af0] mb-1">Projected Y3 ARR</div>
                <div className="text-2xl font-bold text-[#059669]" style={{ fontFamily: "'DM Sans', sans-serif" }}>
                  {featuredIdea.estimated_arr || "$150K"}
                </div>
                {/* Mini sparkline */}
                <svg className="w-24 h-8 mt-2" viewBox="0 0 100 30">
                  <polyline
                    points="0,28 20,22 40,18 60,12 80,8 100,2"
                    fill="none"
                    stroke="#7c6af0"
                    strokeWidth="2"
                    strokeLinecap="round"
                  />
                </svg>
              </div>

              {/* Center: Content */}
              <div className="flex-1">
                <div className="flex items-center gap-2 mb-2">
                  <span
                    className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-medium"
                    style={{ background: "rgba(124, 106, 240, 0.1)", color: "#7c6af0" }}
                  >
                    <Sparkles className="w-3 h-3" />
                    Idea of the Day
                  </span>
                  {featuredIdea.niche && (
                    <span
                      className="px-2 py-0.5 rounded-full text-[11px]"
                      style={{ background: "rgba(124, 106, 240, 0.1)", color: "#7c6af0" }}
                    >
                      {featuredIdea.niche}
                    </span>
                  )}
                </div>
                <h3
                  className="text-[18px] font-bold text-[#18151f] mb-1"
                  style={{ fontFamily: "'Instrument Serif', serif" }}
                >
                  {featuredIdea.title}
                </h3>
                <p className="text-[13px] text-[#6b5f8a] mb-4 line-clamp-2" style={{ fontFamily: "'DM Sans', sans-serif" }}>
                  {featuredIdea.tagline}
                </p>
                <div className="flex flex-wrap items-center gap-4 text-[12px] text-[#6b5f8a]">
                  <span className="flex items-center gap-1">
                    <TrendingUp className="w-3.5 h-3.5" />
                    Score: {featuredIdea.opportunity_score || featuredIdea.score || 85}
                  </span>
                  <span className="flex items-center gap-1">
                    <Clock className="w-3.5 h-3.5" />
                    MVP: {featuredIdea.mvp_weeks || 4} weeks
                  </span>
                  <span className="flex items-center gap-1">
                    <Users className="w-3.5 h-3.5" />
                    8 competitors
                  </span>
                </div>
              </div>

              {/* Right: Actions */}
              <div className="flex flex-col gap-2 md:w-[140px] flex-shrink-0">
                <Link
                  to={`/idea/${featuredIdea.id}`}
                  className="text-center text-[13px] font-medium text-white px-4 py-2.5 rounded-full transition-colors"
                  style={{ background: "#18151f", fontFamily: "'DM Sans', sans-serif" }}
                >
                  Build This →
                </Link>
                <Link
                  to={`/idea/${featuredIdea.id}`}
                  className="text-center text-[13px] font-medium text-[#7c6af0] px-4 py-2 rounded-full border border-[rgba(124,106,240,0.3)] hover:bg-[rgba(124,106,240,0.05)] transition-colors"
                  style={{ fontFamily: "'DM Sans', sans-serif" }}
                >
                  View Report
                </Link>
              </div>
            </div>
          </div>
        )}

        {/* Ideas List (Horizontal Rows) */}
        <div className="space-y-4">
          {isLoading ? (
            <div className="text-center py-16">
              <div className="inline-block h-10 w-10 animate-spin rounded-full border-4 border-[#7c6af0] border-r-transparent" />
              <p className="mt-4 text-[#6b5f8a]">Loading startup blueprints...</p>
            </div>
          ) : filteredIdeas.length === 0 ? (
            <div className="text-center py-16">
              <Search className="h-12 w-12 text-[#6b5f8a] mx-auto mb-4 opacity-50" />
              <h3 className="text-xl font-semibold text-[#18151f] mb-2">No ideas found</h3>
              <p className="text-[#6b5f8a]">Try adjusting your filters or search terms</p>
            </div>
          ) : (
            filteredIdeas.map((idea: any) => (
              <div
                key={idea.id}
                className="rounded-2xl overflow-hidden flex flex-col md:flex-row"
                style={{
                  background: "rgba(255, 255, 255, 0.72)",
                  backdropFilter: "blur(12px)",
                  border: "1px solid rgba(255, 255, 255, 0.85)",
                }}
              >
                {/* Left: ARR Panel */}
                <div
                  className="w-full md:w-[200px] flex-shrink-0 p-4 flex flex-col items-center justify-center"
                  style={{ background: "linear-gradient(135deg, rgba(168, 152, 248, 0.2), rgba(204, 130, 232, 0.15))" }}
                >
                  <svg className="w-20 h-8 mb-2" viewBox="0 0 80 30">
                    <polyline
                      points="0,25 15,20 30,15 45,12 60,8 80,3"
                      fill="none"
                      stroke="#7c6af0"
                      strokeWidth="2"
                      strokeLinecap="round"
                    />
                  </svg>
                  <div className="text-lg font-bold text-[#059669]" style={{ fontFamily: "'DM Sans', sans-serif" }}>
                    {idea.estimated_arr || "$100K"}
                  </div>
                  <div className="text-[10px] text-[#6b5f8a]">Projected Y3 ARR</div>
                </div>

                {/* Center: Content */}
                <div className="flex-1 p-5">
                  <div className="flex flex-wrap items-center gap-2 mb-2">
                    {idea.niche && (
                      <span
                        className="px-2 py-0.5 rounded-full text-[11px]"
                        style={{ background: "rgba(124, 106, 240, 0.1)", color: "#7c6af0" }}
                      >
                        {idea.niche}
                      </span>
                    )}
                    {idea.score && (
                      <span
                        className="px-2 py-0.5 rounded-full text-[11px]"
                        style={{
                          background: idea.score >= 70 ? "rgba(16, 185, 129, 0.1)" : idea.score >= 40 ? "rgba(245, 158, 11, 0.1)" : "rgba(239, 68, 68, 0.1)",
                          color: idea.score >= 70 ? "#059669" : idea.score >= 40 ? "#d97706" : "#dc2626",
                        }}
                      >
                        {idea.score >= 70 ? "Easy" : idea.score >= 40 ? "Medium" : "Hard"}
                      </span>
                    )}
                  </div>
                  <h3
                    className="text-[16px] font-bold text-[#18151f] mb-1"
                    style={{ fontFamily: "'Instrument Serif', serif" }}
                  >
                    {idea.title}
                  </h3>
                  <p className="text-[13px] text-[#6b5f8a] mb-3 line-clamp-2" style={{ fontFamily: "'DM Sans', sans-serif" }}>
                    {idea.tagline || idea.summary || idea.description}
                  </p>
                  <div className="flex flex-wrap items-center gap-4 text-[11px] text-[#6b5f8a]">
                    <span className="flex items-center gap-1">
                      <TrendingUp className="w-3 h-3" />
                      {idea.market_size || "$1B+"}
                    </span>
                    <span className="flex items-center gap-1">
                      <Clock className="w-3 h-3" />
                      {idea.mvp_weeks ? `${idea.mvp_weeks}w MVP` : "4w MVP"}
                    </span>
                    <span className="flex items-center gap-1">
                      <Users className="w-3 h-3" />
                      {idea.score >= 70 ? "Easy" : idea.score >= 40 ? "Medium" : "Hard"}
                    </span>
                    {idea.opportunity_score && (
                      <span>
                        Opportunity: {idea.opportunity_score}
                      </span>
                    )}
                  </div>
                </div>

                {/* Right: Actions */}
                <div className="w-full md:w-[140px] flex-shrink-0 p-4 flex flex-col justify-center gap-2 border-t md:border-t-0 md:border-l border-white/50">
                  <Link
                    to={`/idea/${idea.id}`}
                    className="text-center text-[13px] font-medium text-white px-4 py-2.5 rounded-full transition-colors"
                    style={{ background: "#18151f", fontFamily: "'DM Sans', sans-serif" }}
                  >
                    Build This →
                  </Link>
                  <Link
                    to={`/idea/${idea.id}`}
                    className="text-center text-[13px] text-[#7c6af0] hover:underline"
                    style={{ fontFamily: "'DM Sans', sans-serif" }}
                  >
                    View Report
                  </Link>
                  <button className="text-center text-[12px] text-[#6b5f8a] hover:text-[#7c6af0] transition-colors">
                    ♡ Save
                  </button>
                </div>
              </div>
            ))
          )}
        </div>

        {/* Pagination */}
        {!isLoading && !searchQuery.trim() && totalPages > 1 && filteredIdeas.length > 0 && (
          <div className="mt-10 flex items-center justify-center gap-4">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
              disabled={currentPage === 1}
              className="border-white/80 bg-white/60 text-[#18151f] hover:bg-white/80"
            >
              <ChevronLeft className="h-4 w-4 mr-1" />
              Previous
            </Button>

            <span className="text-[13px] text-[#6b5f8a]">
              Page {currentPage} of {totalPages}
            </span>

            <Button
              variant="outline"
              size="sm"
              onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
              disabled={currentPage === totalPages}
              className="border-white/80 bg-white/60 text-[#18151f] hover:bg-white/80"
            >
              Next
              <ChevronRight className="h-4 w-4 ml-1" />
            </Button>
          </div>
        )}

        <Footer />
      </div>
    </div>
  );
};

export default StartupExplorer;
